"use client";

import type {
	AnchorHTMLAttributes,
	ButtonHTMLAttributes,
	DetailedHTMLProps,
	FC,
	ReactNode,
} from "react";
import { isValidElement } from "react";
import type { Placement } from "react-aria";
import type {
	ButtonProps as AriaButtonProps,
	LinkProps as AriaLinkProps,
} from "react-aria-components";
import { Button as AriaButton, Link as AriaLink } from "react-aria-components";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";

export const styles = {
	secondary:
		"bg-primary text-fg-quaternary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-fg-quaternary_hover disabled:shadow-xs disabled:ring-disabled_subtle aria-disabled:shadow-xs aria-disabled:ring-disabled_subtle",
	tertiary:
		"text-fg-quaternary hover:bg-primary_hover hover:text-fg-quaternary_hover",
};

export type ButtonUtilityDisabledBehavior = "native" | "focusable";

const focusableDisabledStyles = {
	secondary:
		"cursor-not-allowed! text-fg-disabled_subtle hover:bg-primary hover:text-fg-disabled_subtle",
	tertiary:
		"cursor-not-allowed! text-fg-disabled_subtle hover:bg-transparent hover:text-fg-disabled_subtle",
};

/** Handlers removidos quando o botão está indisponível e focável. */
const PRESS_HANDLER_KEYS = [
	"onClick",
	"onPress",
	"onPressStart",
	"onPressEnd",
	"onPressUp",
	"onPressChange",
] as const;

/**
 * Common props shared between button and anchor variants
 */
export type CommonProps = {
	/** Disables the button and shows a disabled state */
	isDisabled?: boolean;
	disabledBehavior?: ButtonUtilityDisabledBehavior;
	/** The size variant of the button */
	size?: "xs" | "sm";
	/** The color variant of the button */
	color?: "secondary" | "tertiary";
	/** The icon to display in the button */
	icon?: FC<{ className?: string }> | ReactNode;
	/** The tooltip to display when hovering over the button */
	tooltip?: string;
	/** The placement of the tooltip */
	tooltipPlacement?: Placement;
};

/**
 * Props for the button variant (non-link)
 */
export type ButtonProps = CommonProps &
	DetailedHTMLProps<
		Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color" | "slot">,
		HTMLButtonElement
	> & {
		/** Slot name for react-aria component */
		slot?: AriaButtonProps["slot"];
	};

/**
 * Props for the link variant (anchor tag)
 */
interface LinkProps
	extends CommonProps,
		DetailedHTMLProps<
			Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "color">,
			HTMLAnchorElement
		> {
	/** Options for the configured client side router. */
	routerOptions?: AriaLinkProps["routerOptions"];
}

/** Union type of button and link props */
export type Props = ButtonProps | LinkProps;

type ForwardedProps = Omit<
	Props,
	| "tooltip"
	| "className"
	| "isDisabled"
	| "disabledBehavior"
	| "icon"
	| "size"
	| "color"
	| "tooltipPlacement"
>;

function resolveComponentProps(
	otherProps: ForwardedProps,
	href: string | undefined,
	isDisabled: boolean,
	isFocusableWhenDisabled: boolean
): Record<string, unknown> {
	if (href) {
		return {
			...otherProps,

			href: isDisabled ? undefined : href,
			isDisabled,

			...(isDisabled ? { "data-rac": true, "data-disabled": true } : {}),
		};
	}

	if (isFocusableWhenDisabled) {
		const enabledProps: Record<string, unknown> = { ...otherProps };
		for (const key of PRESS_HANDLER_KEYS) {
			delete enabledProps[key];
		}

		return {
			...enabledProps,

			type: "button",
			isDisabled: false,
			"aria-disabled": true,
		};
	}

	return {
		...otherProps,

		type: ("type" in otherProps && otherProps.type) || "button",
		isDisabled,
	};
}

export const ButtonUtility = ({
	tooltip,
	className,
	isDisabled,
	disabledBehavior = "native",
	icon: Icon,
	size = "sm",
	color = "secondary",
	tooltipPlacement = "top",
	...otherProps
}: Props) => {
	const href = "href" in otherProps ? otherProps.href : undefined;
	const Component = href ? AriaLink : AriaButton;
	const isFocusableWhenDisabled =
		!href && !!isDisabled && disabledBehavior === "focusable";

	const isTooltipDisabled = isFocusableWhenDisabled ? false : isDisabled;
	const focusableDisabledClassName = isFocusableWhenDisabled
		? focusableDisabledStyles[color]
		: "";

	const props = resolveComponentProps(
		otherProps,
		href,
		!!isDisabled,
		isFocusableWhenDisabled
	);

	const content = (
		<Component
			aria-label={tooltip}
			{...props}
			className={cx(
				"group relative inline-flex h-max cursor-pointer items-center justify-center rounded-md p-1.5 outline-focus-ring transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:text-fg-disabled_subtle",
				"disabled:cursor-not-allowed!",
				styles[color],

				// Icon styles
				"*:data-icon:pointer-events-none *:data-icon:shrink-0 *:data-icon:text-current *:data-icon:transition-inherit-all",
				size === "xs" ? "*:data-icon:size-4" : "*:data-icon:size-5",

				className,

				focusableDisabledClassName
			)}
		>
			{isReactComponent(Icon) && <Icon data-icon />}
			{isValidElement(Icon) && Icon}
		</Component>
	);

	if (tooltip) {
		return (
			<Tooltip
				isDisabled={isTooltipDisabled}
				offset={size === "xs" ? 4 : 6}
				placement={tooltipPlacement}
				title={tooltip}
			>
				{content}
			</Tooltip>
		);
	}

	return content;
};
